import { useData, useTeams } from '@microsoft/teamsfx-react';
import { useGraphWithCredential } from '@microsoft/teamsfx-react';
import { Providers, ProviderState } from '@microsoft/mgt-element';
import { TeamsFxProvider } from '@microsoft/mgt-teamsfx-provider';
import { useContext, useMemo } from 'react';
import { TeamsFxContext } from '../context';
import { TeamsUserCredential } from '@microsoft/teamsfx';
import { Client } from '@microsoft/microsoft-graph-client';
import { IPPPApp, discoverEnvironments, getLaunchUrls, getTPGApps, isConfigTab } from '../services';
import { ClientTypeError, DiscoveryError, IInstance, ILaunchUrl, LoginData } from '../interfaces';
import { HostClientType } from '@microsoft/teams-js';
import config from '../config';

export const useGraph = (successCallback: () => void) => {
	useContext(TeamsFxContext); // keep subscription for theme updates
	const teamsUserCredential = useMemo(
		() => new TeamsUserCredential({ initiateLoginEndpoint: config.initiateLoginEndpoint, clientId: config.clientId! }),
		[]
	);
	const [teamsContext] = useTeams();
	const clientType = teamsContext.context?.app.host.clientType;

	const url = getLaunchUrls('default');

	// Scopes 'openid', 'profile', 'email' added automatically by Teams
	const scope = ['User.Read'];

	let environmentScope = `${url.host}/user_impersonation`;
	if (!isConfigTab()) {
		scope.push(environmentScope);
	}

	const { loading, error, data, reload } = useGraphWithCredential(
		async (graph, teamsUserCredential, scope) => {
			if (!clientType) {
				return { error: new ClientTypeError('Client type not valid') };
			}
			return verifyCredentials(graph, teamsUserCredential, scope, successCallback, url, clientType);
		},
		{
			scope,
			credential: teamsUserCredential,
		}
	);

	return {
		loading,
		data,
		error,
		reload,
		teamsUserCredential,
	};

};

const verifyCredentials = async (
	graph: Client,
	teamsUserCredential: TeamsUserCredential,
	scope: string[],
	successCallback: () => void,
	url: ILaunchUrl,
	clientType: HostClientType
): Promise<LoginData> => {
	let profile: any = undefined;
	let environments: IInstance[] = [];
	let pppApps: IPPPApp[] = [];

	try {
		// Call graph api directly to get user profile information
		profile = await verifyGraphConnection(graph, teamsUserCredential, scope);

		environments = await discoverEnvironments(teamsUserCredential);

		verifyDataverseConnection(environments, url, clientType);
		if (url.isDynamics) {
			pppApps = await getTPGApps(teamsUserCredential, { Url: `${url.host}` } as any);
		}

		successCallback();

		return { profile, environments, pppApps };
	} catch (error: any) {
		console.error('useGraph error', error);
		console.debug('useGraph error teamsUserCredential:', teamsUserCredential);

		// Check for UI required error: "code": "Pi.UiRequiredError" - UI login prompt required
		if (error instanceof DiscoveryError || error.code?.includes('UiRequiredError')) {
			try {
				await teamsUserCredential.login(scope);
				const profile = await verifyGraphConnection(graph, teamsUserCredential, scope);
				const environments = await discoverEnvironments(teamsUserCredential);

				verifyDataverseConnection(environments, url, clientType);
				if (url.isDynamics) {
					pppApps = await getTPGApps(teamsUserCredential, { Url: `${url.host}` } as any);
				}

				successCallback();

				return { profile, environments };
			} catch (error: any) {
				console.error('useGraph retry ui login error', error, teamsUserCredential);
				return { error, profile, environments, pppApps };
			}
		}

		return { error, profile, environments, pppApps };
	}
};

// Check discovered environments against configured URL
const verifyDataverseConnection = (environments: IInstance[], url: ILaunchUrl, clientType?: string) => {
	let foundEnvironment = false;
	// Find the current environment URL
	environments.forEach((env) => {
		if (!url?.isDynamics) {
			foundEnvironment = true;
		}
		if (url?.host && url?.host.toLocaleLowerCase() === env.Url?.toLocaleLowerCase()) {
			// Env found
			console.debug('Environment discovered:', env);
			foundEnvironment = true;
		}
	});

	if (!foundEnvironment) {
		const message = `Environment in app URL ${url?.host} was not found by discovery service, you may not have access. Try to sign-in again.`;
		console.warn(message);
		console.warn('Note: Guest accounts require tenantId to be provided in app manifest Urls');

		// // Only for desktop throw this to force re-login
		// if (clientType === 'desktop') {
		// 	throw new DiscoveryError(message, 'EnvNotFound');
		// }
	}
};

async function verifyGraphConnection(graph: Client, teamsUserCredential: TeamsUserCredential, scope: string[]) {
	// Initialize Graph Toolkit TeamsFx provider
	const provider = new TeamsFxProvider(teamsUserCredential, scope);
	Providers.globalProvider = provider;

	const profile = await graph.api('/me').get();
	console.debug('Profile:', profile);

	Providers.globalProvider.setState(ProviderState.SignedIn);

	return profile;
}


